import React, { useState, useEffect } from "react";
import { graphql } from "gatsby";
import RichTextComponent from "./richTextComponent";
import Selection from "./selection";
import { eventDonationFieldInteraction, eventFormStart, eventFormFieldInteraction, PROTECT_FIELD_VALUE } from "../../utilities/dataLayer";
import AmountInput from "../inputs/AmountInput";
import AmountSeletor from "../newInputs/AmountSelector.js";
import TextInput from "../inputs/TextInput";
import { setWindow, getWindow, WINDOW_KEYS } from "../../utilities/storage";
import ImpactStatement from "./impactStatement";
import Image from "../base/staticImage";

const FormGroup = ({ formGroup, arrowIcon = null }) => {
  const {
    id,
    formGroupId,
    topRichText,
    isContact,
    heading,
    selection,
    inputs,
    impactStatements,
    monthlyImpactStatementFallback,
    onceImpactStatementFallback,
    bottomBoldText,
    bottomSubText,
  } = formGroup;
  const inputsRef = [...inputs];
  const [activeInputs, setActiveInputs] = useState(inputsRef);

  // original:
  // const handleSelection = (selection, option) => {
  //   eventFormFieldInteraction(
  //     heading,
  //     isContact ? "Contact form" : "Donation form",
  //     option,
  //     selection
  //   );
  //   const updatedInputs = inputsRef.map(field => {
  //     if (field.tags.includes(selection)) {
  //       return field;
  //     } else {
  //       return { ...field, disabled: true };
  //     }
  //   });
  //   setActiveInputs(updatedInputs);
  // };

  //update inputs when selection tab updated.
  const handleSelectionUpdate = (activeSelection) => {
    if (!activeSelection) return;
    eventFormFieldInteraction(heading, isContact ? "Contact form" : "Donation form", selection.option, activeSelection);
    const updatedInputs = [...inputs].map((field) => {
      if (field.tags.includes(activeSelection)) {
        return field;
      } else {
        return { ...field, disabled: true };
      }
    });
    setActiveInputs(updatedInputs);
  };

  // original:
  // useEffect(function initFormState() {
  //   if (!selection) return;
  //   const defaultSelection = selection.defaultValue;
  //   const updatedInputs = inputsRef.map(field => {
  //     if (field.tags.includes(defaultSelection)) {
  //       return { ...field, validation: true, validationShow: false };
  //     } else {
  //       return {
  //         ...field,
  //         disabled: true,
  //         validation: true,
  //         validationShow: false
  //       };
  //     }
  //   });
  //   setActiveInputs(updatedInputs);
  // }, []);

  //This should only be rendered once, when the form is loaded, so do not add any dependencies
  //This is kept because the default initiation might be different when clicking the selection
  useEffect(
    function initFormState() {
      if (!selection) return;
      const defaultSelection = selection.defaultValue;
      const updatedInputs = inputsRef.map((field) => {
        if (field.tags.includes(defaultSelection)) {
          return { ...field, validation: true, validationShow: false };
        } else {
          return {
            ...field,
            disabled: true,
            validation: true,
            validationShow: false,
          };
        }
      });
      setActiveInputs(updatedInputs);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleFormBlur = (event) => {
    // console.log("handle form blur on form group", event);
    const target = event.target;
    if (target.id === "email") {
      sessionStorage.setItem("roktEmail", target.value);
    }

    //remove form start cache on click away from form.
    const currentTarget = event.currentTarget;
    requestAnimationFrame(() => {
      if (!currentTarget.contains(document.activeElement)) {
        setWindow(WINDOW_KEYS.CACHE_FORM_START, null);
      }
    });
  };

  const handleFormFocus = (event) => {
    // console.log("handle form focus on form group", event);
    const target = event.target;
    if (target.tagName.toLowerCase() !== "button") {
      // console.log(heading, isContact);
      eventDonationFieldInteraction(event);

      //We cache form start on focus, and remove the cache on blur.
      //So form will only start once, until user click outside of the form.
      if (getWindow(WINDOW_KEYS.CACHE_FORM_START) !== heading) {
        eventFormStart(heading, isContact ? "Contact form" : "Donation form");
        setWindow(WINDOW_KEYS.CACHE_FORM_START, heading);
      }
    }
  };

  const handleClick = (event) => {
    // console.log("handle form click on form group", event);
    const target = event.target;
    if (target.tagName.toLowerCase() === "button" && target.parentNode.hasAttribute("selectorgroup")) {
      eventDonationFieldInteraction(event);
    }
  };

  const handleFieldBlur = (event, name, isTextInput = false) => {
    let fieldValue;
    if (isTextInput) {
      fieldValue = PROTECT_FIELD_VALUE;
    } else if (typeof event?.target.checked !== "undefined") {
      fieldValue = event.target.checked ? "checked" : "unchecked";
    } else {
      fieldValue = event?.target.value;
    }
    eventFormFieldInteraction(heading, isContact ? "Contact form" : "Donation form", name, fieldValue);
  };

  // The div element having click event binding is because there are inputs inside of it.
  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
  return (
    <div id={formGroupId ?? id} className="form-group donation-form" onBlur={handleFormBlur} onFocus={handleFormFocus} onClick={handleClick}>
      {/* {topRichText ? "" : <hr className={isContact ? "hide" : ""} />} */}
      <div className="wrapper">
        {!isContact ? (
          <h2 className="h4">
            {heading} <span className="require"> (Required) </span>
          </h2>
        ) : null}
        {/** change from "set-frist", update scss as well */}
        <div className={"body" + (topRichText ? " set-first" : "")}>
          {topRichText ? (
            <div className="donate-topRichText">
              <RichTextComponent data={topRichText} />
              <ArrowIcon arrowIcon={arrowIcon} selection={selection} />
            </div>
          ) : null}

          {selection ? (
            <Selection
              data={selection}
              selectorgroup={selection.option}
              onClickCallBack={handleSelectionUpdate}
              defaultOptions={selection.tags}
              defaultValue={selection.defaultValue}
              show={selection.show}
            />
          ) : null}

          {activeInputs.map(
            (
              {
                name,
                uniqueId,
                type,
                tags,
                hideWhenFrequencyIs,
                disabled,
                required,
                options,
                validation,
                validationShow,
                msgValidation,
                showValidaiton,
              },
              index
            ) => {
              switch (type) {
                case "email":
                  return (
                    <TextInput
                      name={name}
                      type={type}
                      required={required}
                      disabled={disabled}
                      key={`${index}-email-form-group`}
                      validation={validation}
                      validationShow={validationShow}
                      msgValidation={msgValidation}
                      showValidaiton={showValidaiton}
                      uniqueId={uniqueId}
                      onBlurChanged={handleFieldBlur}
                    />
                  );
                case "amount":
                  return (
                    <AmountInput
                      name={name}
                      type="text"
                      required={required}
                      disabled={disabled}
                      key={`${index}-amount-form-group`}
                      // donationAmount={donationAmount}
                      uniqueId={uniqueId}
                      onBlurChanged={(e) => {
                        eventDonationFieldInteraction(e);
                        handleFieldBlur(e, name);
                      }}
                    />
                  );
                // removed, because not in use and should be replaced all by amount-selector or selection
                // case "selector":
                //   return (
                //     <Selection
                //       name={name}
                //       type={type}
                //       required={required}
                //       disabled={disabled}
                //       options={options}
                //       key={index}
                //       onBlurChanged={handleFieldBlur}
                //     />
                //   );
                case "amount-selector":
                  return (
                    <AmountSeletor
                      selectorgroup="amount-selector" // dataLayer={"amount-selector"}
                      // name={name}
                      tags={tags}
                      // type="selector"
                      // required={required}
                      disabled={disabled}
                      options={options}
                      key={`${index}-amount-selector-form-group`}
                      // setDonationAmount={setDonationAmount}
                      // donationFixedProcessingCost={donationFixedProcessingCost}
                      // donationAmount={donationAmount}
                    />
                  );
                case "text":
                default:
                  return (
                    <TextInput
                      name={name}
                      type={type}
                      required={required}
                      disabled={disabled}
                      key={`${index}-general-text-form-group`}
                      validation={validation}
                      validationShow={validationShow}
                      msgValidation={msgValidation}
                      showValidaiton={showValidaiton}
                      // donationAmount={donationAmount}
                      // donationPercent={donationPercent}
                      hideWhenFrequencyIs={hideWhenFrequencyIs}
                      uniqueId={uniqueId}
                      options={options}
                      onBlurChanged={handleFieldBlur}
                    />
                  );
              }
            }
          )}

          <ImpactStatement
            monthlyImpactStatementFallback={monthlyImpactStatementFallback}
            onceImpactStatementFallback={onceImpactStatementFallback}
            impactStatements={impactStatements}
            bottomBoldText={bottomBoldText}
            bottomSubText={bottomSubText}
          />
        </div>
      </div>
    </div>
  );
  /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
};

export default FormGroup;

const ArrowIcon = ({ arrowIcon, selection = null }) => {
  //if arrowIcon provided from parent, use the file directly
  if (arrowIcon && arrowIcon?.file?.url) return <img alt="donate arrow" src={arrowIcon.file.url} className="donate-topRichText__arrow" />;
  //if arrowIcon is not provided, however the selection contains "monthly", render the icon ourself
  if (selection && selection.tags && selection.tags.includes("monthly")) return <Image src="Arrow.png" className="donate-topRichText__arrow" />;
  return null;
};

// selection
// defaultValye is default value of this selection
// option is the name of this selection
// tags are used as options for selection
export const query = graphql`
  fragment FormGroupComponent on ContentfulFormGroup {
    id
    heading
    isContact
    donationPercent
    donationFixedProcessingCost
    bottomSubText
    bottomBoldText
    defaultAmount
    formGroupId
    topRichText {
      ...RichTextComponent
    }
    selection {
      option
      tags
      defaultValue
      show
    }
    inputs {
      name
      type
      tags
      hideWhenFrequencyIs
      required
      options
      showValidaiton
      msgValidation
      uniqueId
    }
    monthlyImpactStatementFallback {
      monthlyImpactStatementFallback
    }
    onceImpactStatementFallback {
      onceImpactStatementFallback
    }
    impactStatements {
      ...ImpactStatement
    }
    bottomRichText {
      ...RichTextComponent
    }
  }
`;
